import { isEmpty, isNone, typeOf } from '@ember/utils';
import fromPairs from 'lodash.frompairs';
import { isNumeric } from './numbers';

export function isObjectEqual(a: Record<string, any>, b: Record<string, any>) {
  return Object.keys(a).every((key) => !isNone(a[key]) && a[key] === b[key]);
}

export function isEmptyOrEmptyObject(obj: any) {
  // @ts-ignore: `object` is not a typed option
  if (typeOf(obj) !== 'object') {
    return isEmpty(obj);
  }

  return Object.keys(obj).length === 0;
}

export function areObjectsEqual(a: Record<string, any>, b: Record<string, any>) {
  let aKeys = Object.keys(a);
  let bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) {
    return false;
  }

  return isObjectEqual(a, b) && isObjectEqual(b, a);
}

export function pruneObject(a: Record<string, any>) {
  return fromPairs(Object.entries(a).filter(([_, value]) => !isEmpty(value)));
}

export function extractKeys(obj: Record<string, any>, prefix = ''): string[] {
  let keys = Object.keys(obj);

  return keys.reduce<string[]>((acc, key) => {
    if (key.includes('.')) {
      key = `"${key}"`;
    }

    // @ts-ignore - typeOf isn't typed very well
    if (typeOf(obj[key]) === 'object') {
      return acc.concat(`${prefix}${key}`).concat(extractKeys(obj[key], `${prefix}${key}.`));
    } else {
      return acc.concat(`${prefix}${key}`);
    }
  }, []);
}

export function extractKeyPaths(obj: Record<string, any>, parentKeys: string[] = []): string[][] {
  let keys = Object.keys(obj);

  return keys.reduce<string[][]>((acc, key) => {
    // @ts-ignore - typeOf isn't typed very well
    if (typeOf(obj[key]) === 'object') {
      return [...acc, [...parentKeys, key], ...extractKeyPaths(obj[key], [...parentKeys, key])];
    } else {
      return [...acc, [...parentKeys, key]];
    }
  }, []);
}

export function extractKeyPathsWithIndices(
  obj: Record<string, any> | any[],
  parentKeys: (string | number)[] = [],
): (string | number)[][] {
  let keys: (string | number)[] = [];

  if (typeOf(obj) === 'array') {
    keys = Array.from(obj.keys());
  } else {
    keys = Object.keys(obj);
  }

  return keys.reduce<(string | number)[][]>((acc, key) => {
    // @ts-ignore - typeOf isn't typed very well
    if (typeOf(obj[key]) === 'object') {
      return [...acc, [...parentKeys, key], ...extractKeyPathsWithIndices((obj as any)[key], [...parentKeys, key])];
    } else if (typeOf((obj as any)[key]) === 'array') {
      return [...acc, [...parentKeys, key], ...extractKeyPathsWithIndices((obj as any)[key], [...parentKeys, key])];
    } else {
      return [...acc, [...parentKeys, key]];
    }
  }, []);
}

// This is a slightly modified version of the lodash.topath utility.
// The original returns strings for array indices, but we specifically want to
// use number types for them, so we make sure to `parseInt` those indices.
let reEscapeChar = /\\(\\)?/g;
let rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;
export function stringToPath(value: string | number): (string | number)[] {
  let result = [];

  if (typeof value === 'number') {
    return [value];
  }

  if (value.charCodeAt(0) === 46 /* . */) {
    result.push('');
  }
  value.replace(
    rePropName,
    (match: string, number: string | undefined, quote: string | undefined, subString: string | undefined) => {
      let _number: string | number | undefined;
      if (number != null) {
        _number = parseInt(number);
      }
      result.push(quote ? subString?.replace(reEscapeChar, '$1') : _number ?? match);
      return '';
    },
  );
  return result;
}

// The regex is a series of OR conditions to match:
// \s - match whitespace
// \. - match a period
// \/ - match a slash
// \((.*)\) - match () brackets, with or without content
export const reStringNeedsEscaping = /\s|\.|\/|\((.*)\)/;

export function keyPathToString(path: (string | number)[], splitByDot = false): string {
  return path.reduce<string>((acc, pathItem) => {
    if (typeof pathItem === 'number') {
      return `${acc}[${pathItem}]`;
    } else {
      // Cases where nested data structures are used, i.e., https://github.com/customerio/issues/issues/11467
      // we try to split by
      // but we should skip over snippets because they aren't nested data structures
      if (splitByDot && /.+\..+/i.test(pathItem)) {
        let value = keyPathToString(pathItem.split(/\.(?=.)/), splitByDot); // split by "." but leave trailing "."
        if (value[0] === '[') {
          return `${acc}${value}`;
        }
        let newAcc = `${acc}.${value}`;
        if (newAcc[0] === '.') {
          newAcc = newAcc.substring(1);
        }
        return newAcc;
      }

      // Check whether the object key needs to be escaped. If it does, it needs
      // to be accessed via ['object.key'] rather than with parent.object_key
      // notation
      let needsEscaping = isNumeric(pathItem) || reStringNeedsEscaping.test(pathItem);
      if (needsEscaping) {
        // Reference: https://github.com/customerio/opsbugs/issues/3891 - Single quotes are preferred.
        return `${acc}['${pathItem}']`;
      } else {
        let newAcc = `${acc}.${pathItem}`;
        if (newAcc[0] === '.') {
          newAcc = newAcc.substring(1);
        }
        return newAcc;
      }
    }
  }, '');
}

export function prettyJSONStringify(obj: Record<any, any>, space: number | string = 2) {
  return JSON.stringify(obj, null, space);
}

export function flatTransformKeys(obj: any, fn: (k: string) => string): any {
  let objType = typeOf(obj);

  // @ts-ignore Ember types don't recognize 'object' value
  if (objType === 'object') {
    return Object.fromEntries(Object.entries(obj).map(([key, val]) => [fn(key), val]));
  }

  return obj;
}

export function recursiveTransformKeys(obj: any, fn: (k: string) => string, depthLevel = Infinity): any {
  let objType = typeOf(obj);

  // prespects max depth level
  if (depthLevel <= 0) {
    return flatTransformKeys(obj, fn);
  }

  // @ts-ignore Ember types don't recognize 'object' value
  if (objType === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([key, val]) => [fn(key), recursiveTransformKeys(val, fn, depthLevel - 1)]),
    );
  } else if (objType === 'array') {
    return obj.map((i: any) => recursiveTransformKeys(i, fn, depthLevel - 1));
  }

  return obj;
}

export function emptyValuesToNull<O = Record<string, any>>(obj: Record<string, any>): O {
  let entries = Object.entries(obj).map(([key, value]) => [key, isEmpty(value) ? null : value]);

  return Object.fromEntries(entries);
}
